import { RootState } from "common/reducers";
import { ids, metaPagination } from "common/selectors/idsManagement.selectors";
import { IdType } from "common/types";
import { createSelector } from "reselect";
import { currentOrg } from "common/selectors/org.selectors";
import { ActivatedPriceListRecord } from "common/records/ActivatedPriceList";
import { Nullable } from "common/records";

import {
  model,
  byId,
  activatedPriceListForCurrentEstimate,
} from "common/selectors/activatedPriceList.common.selectors";
export { model, byId, activatedPriceListForCurrentEstimate };

/**
 * Loads the activated price list by the given id
 *
 * @param {RootState} state The RootState
 * @param {string | number} id
 * @returns {ActivatedPriceListRecord} PriceListRecord
 */
export const activatedPriceListById = (state: RootState, id: IdType) => byId(state).get(id as number);

/**
 * The id of the APL whose snapshot is currently fanned into the GLOBAL product stores (a single
 * snapshot lives there at a time). getActivatedPriceList compares against this to decide whether
 * the live globals already reflect the requested APL or must be re-fanned. See getActivatedPriceList.
 *
 * @param {RootState} state The RootState
 * @returns {number | null} the currently-fanned activated_price_list id, or null
 */
export const fannedActivatedPriceListId = (state: RootState): number | null =>
  state.getIn([model, "fannedId"]) as number | null;

/**
 * True when this APL is cached AND is the snapshot currently fanned into the global stores. Gates
 * the Product Selector's loading state and the estimate loader's re-fan guard. The `cached` check
 * distinguishes a SUMMARY record (shares the id, no products) from a loaded snapshot; the fannedId
 * check catches a cached record whose globals were clobbered by another APL — see
 * fannedActivatedPriceListId.
 *
 * @param {RootState} state The RootState
 * @param {number | null | undefined} activatedPriceListId
 * @returns {boolean} true when this APL is cached AND is the snapshot currently fanned into the globals
 */
export const activatedPriceListHydrated = (state: RootState, activatedPriceListId: Nullable<number>): boolean => {
  if (!activatedPriceListId) return false;
  const record = activatedPriceListById(state, activatedPriceListId);
  return (
    !!record &&
    record.id === activatedPriceListId &&
    record.cached === true &&
    fannedActivatedPriceListId(state) === activatedPriceListId
  );
};

/**
 * Gets all activatedPriceListIds for the given priceListId from IdsManagement
 *
 * @param {RootState} state The RootState
 * @param {number} priceListId The priceListId to filter by
 * @returns {List<IdType>} List of activatedPriceListIds
 */
export const activatedPriceListIdsByPriceListId = (state: RootState, priceListId: number) =>
  ids(state, { modelName: `activatedPriceLists.${priceListId}` });

/**
 * Gets the pagination meta for activated price lists of the given priceListId
 *
 * @param {RootState} state The RootState
 * @param {number} priceListId The priceListId
 * @returns {MetaPaginationRecord | null} pagination meta
 */
export const activatedPriceListMetaByPriceListId = (state: RootState, priceListId: number) =>
  metaPagination(state, { modelName: `activatedPriceLists.${priceListId}` });

/**
 * Gets activated price list record for a specific field on org
 *
 * @param {RootState} state The RootState
 * @param {string} field The field on org to access
 * @returns {ActivatedPriceListRecord} activatedPriceListRecord
 */
const getActivatedPriceListForField = (
  field: "activated_price_list_id" | "activated_inspection_list_id" | "current_price_list_id",
) =>
  createSelector([currentOrg, (state) => state], (org, state) => {
    if (!org) {
      return null;
    }

    const currentActivatedPriceListId = org.get(field);
    if (!currentActivatedPriceListId) {
      return null;
    }

    const currentActivatedPriceList = activatedPriceListById(state, currentActivatedPriceListId as number);
    if (!currentActivatedPriceList) {
      return null;
    }

    return currentActivatedPriceList;
  });

/**
 * Gets activated price list record for current org
 *
 * @param {RootState} state The RootState
 * @returns {ActivatedPriceListRecord} activatedPriceListRecord
 */
export const activatedPriceListCurrentOrg = getActivatedPriceListForField("activated_price_list_id");

/**
 * Gets inspection activated price list record for current org
 *
 * @param {RootState} state The RootState
 * @returns {ActivatedPriceListRecord} activatedPriceListRecord
 */
export const inspectionActivatedPriceListCurrentOrg = getActivatedPriceListForField("activated_inspection_list_id");

/**
 * Gets activated price list id for current org
 *
 * @param {RootState} state The RootState
 * @returns {number} activatedPriceListRecord id
 */
export const currentOrgActivatedPriceListId = createSelector([currentOrg], (org) => {
  if (!org) {
    return null;
  }

  return org.get("activated_price_list_id") || null;
});

/**
 * Gets current price list id for current org
 *
 * @param {RootState} state The RootState
 * @returns {number} activatedPriceListRecord id
 */
export const currentOrgCurrentPriceListId = createSelector([currentOrg], (org) => {
  if (!org) {
    return null;
  }

  return org.get("current_price_list_id") || null;
});

/**
 * Gets the current price list record for current org
 *
 * @param {RootState} state The RootState
 * @returns {ActivatedPriceListRecord} activatedPriceListRecord
 */
export const currentOrgCurrentPriceList: (state: RootState) => Nullable<ActivatedPriceListRecord> =
  getActivatedPriceListForField("current_price_list_id");
